import * as React from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import TableauReportView from 'global_elements/TableauReportView/index.js';
import ReportsProxy from 'api/reports/reportsProxy';
import PageLayout from 'global_elements/Layouts/PageLayout';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { ActiveFacilityContext } from 'context/activeFacility';
import { UserContext } from 'context/user';
import { Roles } from 'constants/roles';
import './styles.scss';
import Paragraph from 'global_elements/Text/Paragraph';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import ReportFiltersComponent from 'global_elements/ReportFilters';
import { NavBarVariant } from 'global_elements/Navigation/NavBar/variants';
import { ProgramSelectOption, SelectOption } from 'types/inputProps';
import { ReportParameters } from 'constants/reports';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import InlineText from 'global_elements/Text/InlineText';
import { AllRoutes } from 'constants/routes';
import { ReactComponent as LeftArrow } from 'icons/mho-left-arrow-icon.svg';
import { SelectedReportFilters } from 'types/ReportGlobalFilters';
import { subtractDay } from 'domain/dateTime/DateHelpers';
import SecondaryHeader from 'global_elements/Text/SecondaryHeader';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMediaQuery } from 'react-responsive';
import { ReportSessionData, ReportSessionModes } from 'interfaces/reports/tableauReport';

const ERROR_MESSAGE = 'An error occurred while attempting to load Tableau. Please refresh the page to try again.';
const ERROR_MESSAGE_401 = 'An error occurred while attempting to load the report. Please contact your Administrator.';

const ReportDetailPage = (): React.JSX.Element => {
  const { facility } = React.useContext(ActiveFacilityContext);
  const { user, selectedReportFilters, isNavExpanded, reportTrackingID, SetReportTrackingID, SetIsNavExpanded, SetSelectedReportFilters, SetWorkBookHash } = React.useContext(UserContext);
  const [selectedFacilityId, setSelectedFacilityId] = React.useState<number>(-1);
  const paramSearch = useLocation().search;
  const workBookHash = new URLSearchParams(paramSearch).get('reportID');
  const isCoreMeasures = new URLSearchParams(paramSearch).get('cm');
  const reportName = new URLSearchParams(paramSearch).get('reportname');
  const programFilter = new URLSearchParams(paramSearch).get('pf');
  const programFilterID = new URLSearchParams(paramSearch).get('pfid');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isBuildingUrl, setIsBuildingUrl] = React.useState<boolean>(false);
  const [reportUrlWithParams, setReportUrlWithParams] = React.useState<string | null>(null);
  const [requiredReportParameters, setRequiredReportParameters] = React.useState<string[]>([]);
  const [isLoadingReportParameters, setIsLoadingReportParameters] = React.useState<boolean>(true);
  const [baseUrl, setBaseUrl] = React.useState<string>('');
  const [dateTypeOptions, setDateTypeOptions] = React.useState<SelectOption[]>([]);
  const history = useHistory();
  const [selectedTab, setSelectedTab] = React.useState<string>('');
  const isLaptop = useMediaQuery({
    query: 'only screen and (max-width : 1340px)',
  });
  const [userSessionData, setUserSessionData] = React.useState<ReportSessionData | null>(null);
  const [trackingID, setTrackingID] = React.useState<string>('');

  const getNavbarVariant = (): NavBarVariant => {
    if (isLaptop) {
      return NavBarVariant.PRIMARY;
    }
    return isNavExpanded ? NavBarVariant.ICON_EXPANDED : NavBarVariant.ICON;
  };

  const getCommaSeparatedValues = (options: ProgramSelectOption[] | SelectOption[]): string => {
    let stringValues = '-1';
    for (let i = 0; i < options.length; i += 1) {
      if (options[i].value !== '*') {
        stringValues += `,${options[i].value}`;
      }
    }

    return stringValues;
  };

  const getCommaSeparatedPopTreatIDs = (programs: ProgramSelectOption[]): string => {
    let stringPopTreats = '';
    for (let i = 0; i < programs.length; i += 1) {
      if (programs[i].value !== '*') {
        stringPopTreats += `${programs[i].populationTreatmentID}`;
        if (i !== programs.length - 1) {
          stringPopTreats += ',';
        }
      }
    }

    return stringPopTreats;
  };

  const getTabFacilityHash = (url: string): string | null => {
    const queryIndex = url.indexOf('?');
    const queryString = queryIndex >= 0 ? url.substring(queryIndex + 1) : url;
    const searchParams = new URLSearchParams(queryString);

    return searchParams.get('FID');
  };

  const getTrackingID = (url: string): string => {
    const queryIndex = url.indexOf('?');
    const queryString = queryIndex >= 0 ? url.substring(queryIndex + 1) : url;
    const searchParams = new URLSearchParams(queryString);

    return searchParams.get('TID') ?? '';
  };

  const addSelectedTabToUrl = (): string => {
    // Get url up to view
    const newBaseUrl = baseUrl.substring(0, baseUrl.lastIndexOf('/'));
    // Get url search param string
    const searchParams = baseUrl.substring(baseUrl.indexOf('?'));
    // console.log(`${newBaseUrl}/${selectedTab}${searchParams}`);
    return `${newBaseUrl}/${selectedTab}${searchParams}`;
  };

  const goToReportsHome = (): void => {
    history.push(AllRoutes.REPORTS);
  };

  const buildUrl = (): void => {
    setIsBuildingUrl(true);

    let urlWithParams = '';
    const sessionID = getTrackingID(baseUrl);
    let tmpUserSessionData: ReportSessionData = { trackingID: sessionID };

    if (selectedTab === '') {
      urlWithParams = `${baseUrl}`;
    } else {
      urlWithParams = addSelectedTabToUrl();
      tmpUserSessionData = { ...tmpUserSessionData, viewURL: selectedTab };
    }

    if (requiredReportParameters.includes('AID')) {
      if (user?.role === Roles.CORPORATION) {
        const tableauFacilityHash = getTabFacilityHash(baseUrl);
        if (tableauFacilityHash) {
          urlWithParams += `&AID=${tableauFacilityHash}`;
          tmpUserSessionData = { ...tmpUserSessionData, aid: tableauFacilityHash };
        }
      }
    }

    if (!selectedReportFilters) {
      console.log(urlWithParams);
      setReportUrlWithParams(urlWithParams);
      setUserSessionData(tmpUserSessionData);
      setIsBuildingUrl(false);
      return;
    }

    if (requiredReportParameters.includes('DateTypeParam') && selectedReportFilters.dateType.value !== '') {
      urlWithParams += `&DateTypeParam=${selectedReportFilters.dateType.value}`;
      tmpUserSessionData = { ...tmpUserSessionData, dateType: selectedReportFilters.dateType.label };
    }

    if (requiredReportParameters.includes('ProgramID') && selectedReportFilters.programs.length > 0) {
      const commaSeparatedPrograms = getCommaSeparatedValues(selectedReportFilters.programs);
      urlWithParams += `&ProgramID=${commaSeparatedPrograms}`;
      tmpUserSessionData = { ...tmpUserSessionData, programID: commaSeparatedPrograms };
    }

    if (requiredReportParameters.includes('PopTreatID') && selectedReportFilters.programs.length > 0) {
      const commaSeparatedPopTreats = getCommaSeparatedPopTreatIDs(selectedReportFilters.programs);
      urlWithParams += `&PopTreatID=${commaSeparatedPopTreats}`;
      tmpUserSessionData = { ...tmpUserSessionData, populationTreatmentID: commaSeparatedPopTreats };
    }

    if (requiredReportParameters.includes('TreatmentTypeID') && selectedReportFilters.treatmentTypes.length > 0) {
      const commaSeparatedTreatments = getCommaSeparatedValues(selectedReportFilters.treatmentTypes);
      urlWithParams += `&TreatmentTypeID=${commaSeparatedTreatments}`;
      tmpUserSessionData = { ...tmpUserSessionData, treatmentTypeID: commaSeparatedTreatments };
    }

    if (requiredReportParameters.includes('PopulationTypeID') && selectedReportFilters.populationTypes.length > 0) {
      const commaSeparatedPops = getCommaSeparatedValues(selectedReportFilters.populationTypes);
      urlWithParams += `&PopulationTypeID=${commaSeparatedPops}`;
      tmpUserSessionData = { ...tmpUserSessionData, populationTypeID: commaSeparatedPops };
    }

    if (requiredReportParameters.includes('StartDate') && selectedReportFilters.startDate) {
      urlWithParams += `&StartDate=${selectedReportFilters.startDate.getFullYear()}-${selectedReportFilters.startDate.getMonth() + 1}-${selectedReportFilters.startDate.getDate()}`;
      tmpUserSessionData = {
        ...tmpUserSessionData,
        dataRange: selectedReportFilters.dateRangeTypeDesc,
        startYear: selectedReportFilters.startDate.getFullYear().toString(),
        startMonth: (selectedReportFilters.startDate.getMonth() + 1).toString(),
        startDay: selectedReportFilters.startDate.getDate().toString(),
      };
    }

    if (requiredReportParameters.includes('EndDate') && selectedReportFilters.endDate) {
      urlWithParams += `&EndDate=${selectedReportFilters.endDate.getFullYear()}-${selectedReportFilters.endDate.getMonth() + 1}-${selectedReportFilters.endDate.getDate()}`;
      tmpUserSessionData = {
        ...tmpUserSessionData,
        dataRange: selectedReportFilters.dateRangeTypeDesc,
        endYear: selectedReportFilters.endDate.getFullYear().toString(),
        endMonth: (selectedReportFilters.endDate.getMonth() + 1).toString(),
        endDay: selectedReportFilters.endDate.getDate().toString(),
      };
    }

    if (requiredReportParameters.includes('StartMonth') && selectedReportFilters.startDate) {
      urlWithParams += `&StartMonth=${selectedReportFilters.startDate.getMonth() + 1}`;
      tmpUserSessionData = { ...tmpUserSessionData, startMonth: (selectedReportFilters.startDate.getMonth() + 1).toString() };
    }
    if (requiredReportParameters.includes('StartYear') && selectedReportFilters.startDate) {
      urlWithParams += `&StartYear=${selectedReportFilters.startDate.getFullYear()}`;
      tmpUserSessionData = { ...tmpUserSessionData, startYear: selectedReportFilters.startDate.getFullYear().toString() };
    }
    if (requiredReportParameters.includes('EndMonth') && selectedReportFilters.endDate) {
      urlWithParams += `&EndMonth=${selectedReportFilters.endDate.getMonth() + 1}`;
      tmpUserSessionData = { ...tmpUserSessionData, endMonth: (selectedReportFilters.endDate.getMonth() + 1).toString() };
    }
    if (requiredReportParameters.includes('EndYear') && selectedReportFilters.endDate) {
      urlWithParams += `&EndYear=${selectedReportFilters.endDate.getFullYear()}`;
      tmpUserSessionData = { ...tmpUserSessionData, endYear: selectedReportFilters.endDate.getFullYear().toString() };
    }

    console.log(urlWithParams);
    tmpUserSessionData = { ...tmpUserSessionData, tableauURL: urlWithParams, notes: 'Sending report url with parameters to Tableau server.' };

    setReportUrlWithParams(urlWithParams);
    setUserSessionData(tmpUserSessionData);
    setTimeout(() => {
      setIsBuildingUrl(false);
      SetReportTrackingID(trackingID); // Once full url has been built, save to user context
      SetWorkBookHash(workBookHash ?? '');
    }, 100); // Slight delay so that Tableau component loads with correct url
  };

  const handlePageLeave = (myWorkBookHash: string | null, myTrackingID: string): any => {
    if (myWorkBookHash && myTrackingID !== '') {
      ReportsProxy.sendUserSessionData(selectedFacilityId, myWorkBookHash, [{ trackingID: myTrackingID }], ReportSessionModes.END)
        .then(() => {
          SetReportTrackingID(''); // Clear out report tracking ID after the session is ended
          SetWorkBookHash('');
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  React.useEffect(() => {
    const unlisten = history.listen(() => {
      handlePageLeave(workBookHash, trackingID);
    });
    // This function will be invoked on component unmount and will clean up
    // the event listener.
    return () => {
      unlisten();
    };
  }, [history, trackingID, workBookHash, selectedFacilityId]);

  React.useEffect(() => {
    if (workBookHash && userSessionData) {
      console.log(workBookHash);
      ReportsProxy.sendUserSessionData(selectedFacilityId, workBookHash, [userSessionData], ReportSessionModes.SEND)
        .then(() => {
          console.log('Sent user data');
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [userSessionData, workBookHash]);

  React.useEffect(() => {
    SetIsNavExpanded(false);
  }, [SetIsNavExpanded]);

  React.useEffect(() => {
    const facilityId = parseInt(facility.value, 10);

    if ((selectedFacilityId !== -1 && facilityId === 0 && selectedFacilityId !== 0) || (facilityId !== 0 && selectedFacilityId === 0)) {
      goToReportsHome();
      return;
    }

    setSelectedFacilityId(facilityId);
  }, [facility, user]);

  React.useEffect(() => {
    if (workBookHash) {
      ReportsProxy.getReportParameters(workBookHash)
        .then((response) => {
          // console.log(response);
          const options: string[] = response.map((x) => x.parameterName);
          setRequiredReportParameters(options);
          // Parse date type options
          const dtOptions: SelectOption[] = [];
          let foundSelectedDateType = false;
          for (let i = 0; i < response.length; i += 1) {
            if (response[i].parameterName === ReportParameters.DateType) {
              dtOptions.push({ value: response[i].parameterValue, label: response[i].parameterLabel });
              if (response[i].parameterValue === selectedReportFilters?.dateType?.value) {
                foundSelectedDateType = true;
              }
            }
          }
          setDateTypeOptions(dtOptions);

          if (!foundSelectedDateType && selectedReportFilters) {
            SetSelectedReportFilters({
              ...selectedReportFilters,
              dateType: { label: '', value: '' },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoadingReportParameters(false);
        });
    } else {
      setIsLoadingReportParameters(false);
    }
  }, [workBookHash]);

  const parametersAreValid = (parametersList: SelectedReportFilters | null): boolean => {
    // console.log(parametersList);
    if (!parametersList) {
      const requiresFilters = requiredReportParameters.some(
        (p) => p === ReportParameters.DateType
          || p === ReportParameters.StartDate
          || p === ReportParameters.StartMonth
          || p === ReportParameters.StartYear
          || p === ReportParameters.EndDate
          || p === ReportParameters.EndMonth
          || p === ReportParameters.EndYear
          || p === ReportParameters.ProgramID
          || p === ReportParameters.TreatmentTypeID
          || p === ReportParameters.PopulationTypeID,
      );
      return !requiresFilters;
    }

    for (let i = 0; i < requiredReportParameters.length; i += 1) {
      if (requiredReportParameters[i] === ReportParameters.DateType && parametersList?.dateType.value === '') {
        return false;
      }
      if (
        (requiredReportParameters[i] === ReportParameters.StartDate || requiredReportParameters[i] === ReportParameters.StartMonth || requiredReportParameters[i] === ReportParameters.StartYear)
        && !parametersList?.startDate
      ) {
        return false;
      }
      if (
        (requiredReportParameters[i] === ReportParameters.EndDate || requiredReportParameters[i] === ReportParameters.EndMonth || requiredReportParameters[i] === ReportParameters.EndYear)
        && !parametersList?.endDate
      ) {
        return false;
      }
      if (requiredReportParameters[i] === ReportParameters.ProgramID && parametersList?.programs.length === 0) {
        return false;
      }
      if (requiredReportParameters[i] === ReportParameters.TreatmentTypeID && parametersList?.treatmentTypes.length === 0) {
        return false;
      }
      if (requiredReportParameters[i] === ReportParameters.PopulationTypeID && parametersList?.populationTypes.length === 0) {
        return false;
      }
    }
    return true;
  };

  React.useEffect(() => {
    setIsLoading(true);

    if (selectedFacilityId === -1 || Number.isNaN(selectedFacilityId) || workBookHash === null || isLoadingReportParameters || !parametersAreValid(selectedReportFilters)) {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return;
    }

    ReportsProxy.getReportFromHash(selectedFacilityId, workBookHash)
      .then((reportData) => {
        if (reportData !== null && reportData !== '') {
          // buildUrl(reportData);
          setBaseUrl(reportData);
          setTrackingID(getTrackingID(reportData));
        } else {
          setErrorMessage(ERROR_MESSAGE);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        if (error.status === 401) {
          setErrorMessage(ERROR_MESSAGE_401);
        } else {
          setErrorMessage(ERROR_MESSAGE);
        }

        setIsLoading(false);
      });
  }, [selectedFacilityId, workBookHash, selectedReportFilters, isLoadingReportParameters]);

  React.useEffect(() => {
    if (workBookHash && baseUrl && baseUrl !== '' && !isLoadingReportParameters && parametersAreValid(selectedReportFilters)) {
      if (reportTrackingID !== '') {
        ReportsProxy.sendUserSessionData(selectedFacilityId, workBookHash, [{ trackingID: reportTrackingID }], ReportSessionModes.END).catch((error) => {
          console.log(error);
        });
      }
      buildUrl();
    }
  }, [baseUrl, requiredReportParameters, isLoadingReportParameters]);

  const isValidCoreMeasuresStartDate = (start: Date | null | undefined): boolean => {
    if (!start) {
      return false;
    }
    return start.getDate() === 1;
  };

  const isValidCoreMeasuresEndDate = (end: Date | null | undefined): boolean => {
    if (!end) {
      return false;
    }
    const endYear = end.getFullYear();
    for (let i = 1; i <= 12; i += 1) {
      // Check if end date is the last day of a month
      if (end.getTime() === subtractDay(new Date(`${i}/1/${i === 1 ? endYear + 1 : endYear}`)).getTime()) {
        return true;
      }
    }
    return false;
  };

  return (
    <PageLayout layout={PageLayoutVariant.ICON_NAV} testText="Reports Page" navBarVariant={getNavbarVariant()} extraClasses="report-detail-page-layout" pageTitle="Reports">
      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START}>
        {isLoadingReportParameters ? (
          <div>
            <p>Loading...</p>
          </div>
        ) : (
          <ReportFiltersComponent requiredParameters={requiredReportParameters} dateTypeOptions={dateTypeOptions} programFilter={programFilter} programFilterID={programFilterID} />
        )}

        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.CENTER} extraClasses="report-container">
          <Button
            variant={ButtonVariant.INVISIBLE}
            onClick={() => {
              goToReportsHome();
            }}
          >
            <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.CENTER} extraClasses="back-button-container">
              <LeftArrow />
              <InlineText fontSize={FontSizes.REGULAR} fontColor={FontColors.PRIMARY} text="Back to Reports" />
            </FlexContainer>
          </Button>
          {reportName && (isLoading || isBuildingUrl || !reportUrlWithParams || !parametersAreValid(selectedReportFilters)) && (
            <SecondaryHeader fontColor={FontColors.PRIMARY} text={reportName} marginBottomPx={15} />
          )}
          {isCoreMeasures === '1'
            && ((requiredReportParameters.includes(ReportParameters.StartMonth) && selectedReportFilters?.startDate && !isValidCoreMeasuresStartDate(selectedReportFilters.startDate))
              || (requiredReportParameters.includes(ReportParameters.EndMonth) && selectedReportFilters?.endDate && !isValidCoreMeasuresEndDate(selectedReportFilters.endDate))) && (
            <InlineText fontSize={FontSizes.REGULAR} fontColor={FontColors.PRIMARY} text="Date range adjusted to match this report's available options." extraClasses="cm-date-range-message" />
          )}
          {(isLoading || isBuildingUrl || isLoadingReportParameters) && <Paragraph text="Loading..." fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} testId="loading-report-detail" />}
          {errorMessage !== '' && <Paragraph text={errorMessage} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} />}
          {errorMessage === '' && !isLoading && !isBuildingUrl && !parametersAreValid(selectedReportFilters) && (
            <Paragraph text="Select valid parameters to load report." fontColor={FontColors.PRIMARY} bold fontSize={FontSizes.REGULAR} testId="valid-parameter-message" />
          )}

          {errorMessage === '' && !isLoading && !isBuildingUrl && reportUrlWithParams && parametersAreValid(selectedReportFilters) && trackingID !== '' && (
            <TableauReportView url={reportUrlWithParams} setTab={setSelectedTab} facilityID={selectedFacilityId} workBookHash={workBookHash} trackingID={trackingID} />
          )}
        </FlexContainer>
      </FlexContainer>
    </PageLayout>
  );
};

export default ReportDetailPage;
